import { useEffect, useRef } from 'react';
// NOTE(port): use the expression-free `lottie_light` SVG build instead of the
// default `lottie-web` entry. The full build ships an expression engine that
// calls `eval()` (a bundler warning + CSP hazard); our skill-authored JSON has
// no expressions, so the light build is both safe and meaningfully smaller.
import lottie, { type AnimationItem } from 'lottie-web/build/player/lottie_light';

// Thin wrapper around lottie-web for rendering Bodymovin/Lottie JSON animations
// (authored with the text-to-lottie skill). Pass the imported JSON as
// `animationData`. SVG renderer keeps the vector crisp at any size.
interface LottieProps {
  animationData: unknown;
  loop?: boolean;
  autoplay?: boolean;
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;
}

export default function Lottie({
  animationData,
  loop = true,
  autoplay = true,
  className,
  style,
  ariaLabel,
}: LottieProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const anim = lottie.loadAnimation({
      container: containerRef.current,
      renderer: 'svg',
      loop,
      autoplay,
      animationData,
    });
    animRef.current = anim;
    return () => {
      anim.destroy();
      animRef.current = null;
    };
  }, [animationData, loop, autoplay]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={style}
      role="img"
      aria-label={ariaLabel}
    />
  );
}
